import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, InputLabel, MenuItem, Select, Switch, Tab, Tabs, TextField, Typography } from '@mui/material';
import { toast } from 'react-toastify';
import DataTable from '../../components/design/DataTable.jsx';
import {
  createVoiceProfile,
  getVoiceSettings,
  listNarrationJobs,
  listVoiceProfiles,
  mintPublicAssetLink,
  reprocessNarrationJob,
  retryNarrationJob,
  testVoice,
  updateVoiceProfile,
  updateVoiceSettings
} from '../../services/contentAiService';

function safeJsonParse(text) {
  try {
    const v = JSON.parse(String(text || ''));
    if (!v || typeof v !== 'object') return null;
    return v;
  } catch {
    return null;
  }
}

function prettyJson(v) {
  try {
    return JSON.stringify(v || {}, null, 2);
  } catch {
    return '{}';
  }
}

function clampNumber(v, { min, max, fallback }) {
  const n = Number(v);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(max, Math.max(min, n));
}

const ACTIVE_PROVIDERS = ['elevenlabs', 'playht'];

function coerceProvider(value, fallback = 'elevenlabs') {
  const normalized = String(value || '').trim().toLowerCase();
  return ACTIVE_PROVIDERS.includes(normalized) ? normalized : fallback;
}

export default function VoiceAiPage() {
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState(0);

  const [settings, setSettings] = useState(null);
  const [settingsDraft, setSettingsDraft] = useState({
    preferredProvider: 'elevenlabs',
    fallbackEnabled: true,
    rateLimitRpm: 60,
    concurrencyLimit: 3,
    cacheEnabled: true,
    cacheTtlSeconds: 86400,
    elevenlabsModel: '',
    elevenlabsApiKey: '',
    playhtUserId: '',
    playhtApiKey: '',
    playhtVoiceEngine: '',
    xttsBaseUrl: 'http://host.docker.internal:5002',
    coquiBaseUrl: '',
    kokoroBaseUrl: '',
    kokoroApiKey: ''
  });

  const [profiles, setProfiles] = useState([]);
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const [editingProfileId, setEditingProfileId] = useState(null);
  const [profileForm, setProfileForm] = useState({
    name: '',
    provider: 'elevenlabs',
    providerVoiceId: '',
    language: 'pt-BR',
    gender: '',
    category: '',
    enabled: true,
    defaultsJson: '{\n  "speed": 1,\n  "emotion": 0.5\n}'
  });

  const [testProfileId, setTestProfileId] = useState('');
  const [testText, setTestText] = useState('Teste de narração. [[pause:0.5]] Segundo trecho com pausa.');
  const [testSpeed, setTestSpeed] = useState(1);
  const [testEmotion, setTestEmotion] = useState(0.5);
  const [procLoudness, setProcLoudness] = useState(true);
  const [procTrimSilence, setProcTrimSilence] = useState(true);
  const [procNoiseReduction, setProcNoiseReduction] = useState(true);
  const [testAudioUrl, setTestAudioUrl] = useState('');

  const [jobs, setJobs] = useState([]);
  const [jobsStatus, setJobsStatus] = useState('');
  const [jobsVoiceProfileId, setJobsVoiceProfileId] = useState('');

  const loadSettings = useCallback(async () => {
    try {
      const resp = await getVoiceSettings();
      const data = resp?.data || null;
      setSettings(data);
      if (data) {
        setSettingsDraft((prev) => ({
          ...prev,
          preferredProvider: coerceProvider(data.preferredProvider, 'elevenlabs'),
          fallbackEnabled: data.fallbackEnabled !== false,
          rateLimitRpm: Number(data.rateLimitRpm || 60),
          concurrencyLimit: Number(data.concurrencyLimit || 3),
          cacheEnabled: data.cacheEnabled !== false,
          cacheTtlSeconds: Number(data.cacheTtlSeconds || 86400),
          elevenlabsModel: data.elevenlabsModel || '',
          playhtUserId: data.playhtUserId || '',
          playhtVoiceEngine: data.playhtVoiceEngine || '',
          xttsBaseUrl: data.xttsBaseUrl || prev.xttsBaseUrl,
          coquiBaseUrl: data.coquiBaseUrl || '',
          kokoroBaseUrl: data.kokoroBaseUrl || '',
          elevenlabsApiKey: '',
          playhtApiKey: '',
          kokoroApiKey: ''
        }));
      }
    } catch (e) {
      toast.error(e?.message || 'Falha ao carregar configurações');
    }
  }, []);

  const loadProfiles = useCallback(async () => {
    try {
      const resp = await listVoiceProfiles();
      const data = resp?.data || [];
      const rows = Array.isArray(data) ? data.filter((row) => ACTIVE_PROVIDERS.includes(String(row?.provider || '').toLowerCase())) : [];
      setProfiles(rows);
    } catch (e) {
      toast.error(e?.message || 'Falha ao carregar vozes');
    }
  }, []);

  const loadJobs = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await listNarrationJobs({
        limit: 100,
        status: jobsStatus || undefined,
        voiceProfileId: jobsVoiceProfileId || undefined
      });
      const data = resp?.data || [];
      setJobs(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(e?.message || 'Falha ao carregar jobs');
    } finally {
      setLoading(false);
    }
  }, [jobsStatus, jobsVoiceProfileId]);

  useEffect(() => {
    loadSettings();
    loadProfiles();
  }, [loadProfiles, loadSettings]);

  useEffect(() => {
    loadJobs();
  }, [loadJobs]);

  const openCreateProfile = () => {
    setEditingProfileId(null);
    setProfileForm({
      name: '',
      provider: 'elevenlabs',
      providerVoiceId: '',
      language: 'pt-BR',
      gender: '',
      category: '',
      enabled: true,
      defaultsJson: '{\n  "speed": 1,\n  "emotion": 0.5\n}'
    });
    setProfileDialogOpen(true);
  };

  const openEditProfile = (row) => {
    const defaults = row?.defaultsJson ? prettyJson(row.defaultsJson) : '{\n  "speed": 1,\n  "emotion": 0.5\n}';
    setEditingProfileId(Number(row?.id || 0) || null);
    setProfileForm({
      name: row?.name || '',
      provider: row?.provider || 'elevenlabs',
      providerVoiceId: row?.providerVoiceId || '',
      language: row?.language || 'pt-BR',
      gender: row?.gender || '',
      category: row?.category || '',
      enabled: row?.enabled !== false,
      defaultsJson: defaults
    });
    setProfileDialogOpen(true);
  };

  const saveProfile = async () => {
    const defaultsObj = safeJsonParse(profileForm.defaultsJson);
    if (!profileForm.name.trim()) {
      toast.error('Informe o nome');
      return;
    }
    if (!profileForm.providerVoiceId.trim()) {
      toast.error('Informe providerVoiceId');
      return;
    }
    if (!defaultsObj) {
      toast.error('defaultsJson inválido');
      return;
    }
    setLoading(true);
    try {
      const payload = {
        name: profileForm.name.trim(),
        provider: profileForm.provider,
        providerVoiceId: profileForm.providerVoiceId.trim(),
        language: profileForm.language || 'pt-BR',
        gender: profileForm.gender || null,
        category: profileForm.category || null,
        enabled: profileForm.enabled === true,
        defaultsJson: defaultsObj
      };
      if (editingProfileId) {
        await updateVoiceProfile(editingProfileId, payload);
        toast.success('Voz atualizada');
      } else {
        await createVoiceProfile(payload);
        toast.success('Voz criada');
      }
      setProfileDialogOpen(false);
      await loadProfiles();
    } catch (e) {
      toast.error(e?.message || 'Falha ao salvar voz');
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setLoading(true);
    try {
      const patch = {
        preferredProvider: settingsDraft.preferredProvider,
        fallbackEnabled: settingsDraft.fallbackEnabled === true,
        rateLimitRpm: clampNumber(settingsDraft.rateLimitRpm, { min: 1, max: 3000, fallback: 60 }),
        concurrencyLimit: clampNumber(settingsDraft.concurrencyLimit, { min: 1, max: 100, fallback: 3 }),
        cacheEnabled: settingsDraft.cacheEnabled === true,
        cacheTtlSeconds: clampNumber(settingsDraft.cacheTtlSeconds, { min: 0, max: 604800, fallback: 86400 }),
        elevenlabsModel: settingsDraft.elevenlabsModel || null,
        playhtUserId: settingsDraft.playhtUserId || null,
        playhtVoiceEngine: settingsDraft.playhtVoiceEngine || null,
        xttsBaseUrl: settingsDraft.xttsBaseUrl || null,
        coquiBaseUrl: settingsDraft.coquiBaseUrl || null,
        kokoroBaseUrl: settingsDraft.kokoroBaseUrl || null
      };
      if (String(settingsDraft.elevenlabsApiKey || '').trim()) patch.elevenlabsApiKey = String(settingsDraft.elevenlabsApiKey).trim();
      if (String(settingsDraft.playhtApiKey || '').trim()) patch.playhtApiKey = String(settingsDraft.playhtApiKey).trim();
      if (String(settingsDraft.kokoroApiKey || '').trim()) patch.kokoroApiKey = String(settingsDraft.kokoroApiKey).trim();
      await updateVoiceSettings(patch);
      setSettingsDraft((prev) => ({ ...prev, elevenlabsApiKey: '', playhtApiKey: '', kokoroApiKey: '' }));
      await loadSettings();
      toast.success('Configurações salvas');
    } catch (e) {
      toast.error(e?.message || 'Falha ao salvar configurações');
    } finally {
      setLoading(false);
    }
  };

  const runTest = async () => {
    const voiceProfileId = Number(testProfileId || 0);
    if (!voiceProfileId) {
      toast.error('Selecione uma voz');
      return;
    }
    if (!testText.trim()) {
      toast.error('Informe o texto');
      return;
    }
    setLoading(true);
    try {
      const resp = await testVoice({
        voiceProfileId,
        text: testText,
        overrides: {
          speed: clampNumber(testSpeed, { min: 0.5, max: 2, fallback: 1 }),
          emotion: clampNumber(testEmotion, { min: 0, max: 1, fallback: 0.5 })
        },
        processing: {
          loudness: procLoudness === true,
          trimSilence: procTrimSilence === true,
          noiseReduction: procNoiseReduction === true
        }
      });
      const assetId = resp?.data?.mediaAssetId || null;
      if (!assetId) throw new Error('Áudio não gerado');
      const link = await mintPublicAssetLink(assetId, 120);
      const url = link?.data?.url || null;
      if (!url) throw new Error('Link público não gerado');
      setTestAudioUrl(url);
      toast.success(resp?.data?.cacheHit ? 'Teste gerado (cache)' : 'Teste gerado');
    } catch (e) {
      toast.error(e?.message || 'Falha no teste');
    } finally {
      setLoading(false);
    }
  };

  const runRetry = async (row) => {
    const id = Number(row?.id || 0);
    if (!id) return;
    setLoading(true);
    try {
      await retryNarrationJob(id);
      toast.success('Job reenfileirado');
      await loadJobs();
    } catch (e) {
      toast.error(e?.message || 'Falha ao reenfileirar');
    } finally {
      setLoading(false);
    }
  };

  const runReprocess = async (row) => {
    const id = Number(row?.id || 0);
    if (!id) return;
    setLoading(true);
    try {
      await reprocessNarrationJob(id, {
        processing: {
          loudness: procLoudness === true,
          trimSilence: procTrimSilence === true,
          noiseReduction: procNoiseReduction === true
        }
      });
      toast.success('Reprocessado');
      await loadJobs();
    } catch (e) {
      toast.error(e?.message || 'Falha ao reprocessar');
    } finally {
      setLoading(false);
    }
  };

  const openOutput = async (row) => {
    const assetId = row?.outputAssetId || row?.outputAsset?.id || null;
    if (!assetId) {
      toast.error('Sem áudio de saída');
      return;
    }
    try {
      const resp = await mintPublicAssetLink(assetId, 120);
      const url = resp?.data?.url || null;
      if (!url) throw new Error('URL não gerada');
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      toast.error(e?.message || 'Falha ao abrir áudio');
    }
  };

  const providers = [
    { key: 'elevenlabs', label: 'ElevenLabs' },
    { key: 'playht', label: 'PlayHT' }
  ];

  const profilesColumns = [
    { key: 'id', label: 'ID' },
    { key: 'name', label: 'Nome' },
    { key: 'provider', label: 'Provider' },
    { key: 'providerVoiceId', label: 'Voice ID' },
    { key: 'language', label: 'Idioma' },
    { key: 'enabled', label: 'Ativo', render: (v) => (v === false ? 'Não' : 'Sim') },
    { key: 'actions', label: 'Ações', render: (_, row) => (
      <Box display="flex" gap={1} flexWrap="wrap">
        <Button size="small" variant="outlined" onClick={() => openEditProfile(row)}>Editar</Button>
        <Button
          size="small"
          variant="contained"
          onClick={() => {
            setTestProfileId(String(row.id));
            setTab(1);
          }}
        >
          Testar
        </Button>
      </Box>
    ) }
  ];

  const jobsColumns = [
    { key: 'id', label: 'ID' },
    { key: 'status', label: 'Status' },
    { key: 'voiceProfile', label: 'Voz', render: (_, row) => row?.voiceProfile?.name || row?.voiceProfileId || '-' },
    { key: 'providerUsed', label: 'Provider' },
    { key: 'durationSeconds', label: 'Duração (s)', render: (v) => (v != null ? Number(v).toFixed(2) : '-') },
    { key: 'lastError', label: 'Erro', render: (v) => (v ? String(v).slice(0, 120) : '-') },
    { key: 'createdAt', label: 'Criado', render: (v) => (v ? new Date(v).toLocaleString() : '-') },
    { key: 'actions', label: 'Ações', render: (_, row) => (
      <Box display="flex" gap={1} flexWrap="wrap">
        <Button size="small" variant="outlined" onClick={() => openOutput(row)} disabled={!row?.outputAssetId && !row?.outputAsset?.id}>Abrir áudio</Button>
        <Button size="small" variant="outlined" onClick={() => runRetry(row)}>Retry</Button>
        <Button size="small" variant="contained" onClick={() => runReprocess(row)} disabled={!row?.rawAssetId && !row?.rawAsset?.id}>Reprocessar</Button>
      </Box>
    ) }
  ];

  return (
    <Box p={2} display="grid" gap={2}>
      <Typography variant="h5">Vozes IA</Typography>

      <Tabs value={tab} onChange={(_, v) => setTab(v)}>
        <Tab label="Configurações" />
        <Tab label="Teste / Preview" />
        <Tab label="Vozes" />
        <Tab label="Jobs" />
      </Tabs>

      {tab === 0 ? (
        <Box display="grid" gap={2} maxWidth={900}>
          <Typography variant="h6">Providers</Typography>
          <FormControl fullWidth>
            <InputLabel>Provider preferido</InputLabel>
            <Select
              label="Provider preferido"
              value={settingsDraft.preferredProvider}
              onChange={(e) => setSettingsDraft((p) => ({ ...p, preferredProvider: e.target.value }))}
            >
              {providers.map((p) => (
                <MenuItem key={p.key} value={p.key}>{p.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
            <Box display="flex" alignItems="center" gap={1}>
              <Switch checked={settingsDraft.fallbackEnabled === true} onChange={(e) => setSettingsDraft((p) => ({ ...p, fallbackEnabled: e.target.checked }))} />
              <Typography>Fallback providers</Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={1}>
              <Switch checked={settingsDraft.cacheEnabled === true} onChange={(e) => setSettingsDraft((p) => ({ ...p, cacheEnabled: e.target.checked }))} />
              <Typography>Cache</Typography>
            </Box>
          </Box>
          <Box display="grid" gap={2} gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }}>
            <TextField
              label="Rate limit (RPM)"
              type="number"
              value={settingsDraft.rateLimitRpm}
              onChange={(e) => setSettingsDraft((p) => ({ ...p, rateLimitRpm: e.target.value }))}
              inputProps={{ min: 1, max: 3000 }}
              fullWidth
            />
            <TextField
              label="Concurrency"
              type="number"
              value={settingsDraft.concurrencyLimit}
              onChange={(e) => setSettingsDraft((p) => ({ ...p, concurrencyLimit: e.target.value }))}
              inputProps={{ min: 1, max: 100 }}
              fullWidth
            />
            <TextField
              label="Cache TTL (segundos)"
              type="number"
              value={settingsDraft.cacheTtlSeconds}
              onChange={(e) => setSettingsDraft((p) => ({ ...p, cacheTtlSeconds: e.target.value }))}
              inputProps={{ min: 0, max: 604800 }}
              fullWidth
            />
            <TextField
              label="ElevenLabs model (opcional)"
              value={settingsDraft.elevenlabsModel}
              onChange={(e) => setSettingsDraft((p) => ({ ...p, elevenlabsModel: e.target.value }))}
              fullWidth
            />
            <TextField
              label="ElevenLabs API Key (somente para atualizar)"
              value={settingsDraft.elevenlabsApiKey}
              onChange={(e) => setSettingsDraft((p) => ({ ...p, elevenlabsApiKey: e.target.value }))}
              fullWidth
            />
            <TextField
              label="PlayHT User ID"
              value={settingsDraft.playhtUserId}
              onChange={(e) => setSettingsDraft((p) => ({ ...p, playhtUserId: e.target.value }))}
              fullWidth
            />
            <TextField
              label="PlayHT API Key (somente para atualizar)"
              value={settingsDraft.playhtApiKey}
              onChange={(e) => setSettingsDraft((p) => ({ ...p, playhtApiKey: e.target.value }))}
              fullWidth
            />
            <TextField
              label="PlayHT voice engine (opcional)"
              value={settingsDraft.playhtVoiceEngine}
              onChange={(e) => setSettingsDraft((p) => ({ ...p, playhtVoiceEngine: e.target.value }))}
              fullWidth
            />
          </Box>
          <Typography variant="body2" color="text.secondary">
            XTTS, Coqui, Kokoro e demais opções locais foram desativados nesta fase de limpeza.
          </Typography>
          <Box display="flex" gap={2} flexWrap="wrap">
            <Button variant="outlined" disabled={loading} onClick={loadSettings}>Recarregar</Button>
            <Button variant="contained" disabled={loading} onClick={saveSettings}>Salvar</Button>
          </Box>
          {settings?.updatedAt ? (
            <Typography variant="body2" color="text.secondary">Atualizado em: {new Date(settings.updatedAt).toLocaleString()}</Typography>
          ) : null}
        </Box>
      ) : null}

      {tab === 1 ? (
        <Box display="grid" gap={2} maxWidth={900}>
          <Typography variant="h6">Preview / Teste</Typography>
          <FormControl fullWidth>
            <InputLabel>Voz</InputLabel>
            <Select label="Voz" value={testProfileId} onChange={(e) => setTestProfileId(e.target.value)}>
              <MenuItem value="">Selecione</MenuItem>
              {profiles.map((p) => (
                <MenuItem key={p.id} value={String(p.id)}>{`#${p.id} - ${p.name} (${p.provider})`}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Texto (use [[pause:0.5]] para pausas)"
            value={testText}
            onChange={(e) => setTestText(e.target.value)}
            fullWidth
            multiline
            minRows={5}
          />
          <Box display="grid" gap={2} gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }}>
            <TextField
              label="Velocidade (0.5 - 2.0)"
              type="number"
              value={testSpeed}
              onChange={(e) => setTestSpeed(e.target.value)}
              inputProps={{ min: 0.5, max: 2, step: 0.05 }}
              fullWidth
            />
            <TextField
              label="Emoção (0 - 1)"
              type="number"
              value={testEmotion}
              onChange={(e) => setTestEmotion(e.target.value)}
              inputProps={{ min: 0, max: 1, step: 0.05 }}
              fullWidth
            />
          </Box>
          <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
            <Box display="flex" alignItems="center" gap={1}>
              <Switch checked={procLoudness === true} onChange={(e) => setProcLoudness(e.target.checked)} />
              <Typography>Normalize loudness</Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={1}>
              <Switch checked={procTrimSilence === true} onChange={(e) => setProcTrimSilence(e.target.checked)} />
              <Typography>Remover silêncio</Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={1}>
              <Switch checked={procNoiseReduction === true} onChange={(e) => setProcNoiseReduction(e.target.checked)} />
              <Typography>Redução ruído</Typography>
            </Box>
          </Box>
          <Box display="flex" gap={2} flexWrap="wrap">
            <Button variant="contained" disabled={loading} onClick={runTest}>Gerar preview</Button>
            <Button variant="outlined" disabled={loading} onClick={() => setTestAudioUrl('')}>Limpar</Button>
          </Box>
          {testAudioUrl ? (
            <Box>
              <Typography variant="subtitle1">Áudio</Typography>
              <audio controls src={testAudioUrl} style={{ width: '100%' }} />
            </Box>
          ) : null}
        </Box>
      ) : null}

      {tab === 2 ? (
        <Box display="grid" gap={2}>
          <Box display="flex" gap={2} flexWrap="wrap">
            <Button variant="contained" disabled={loading} onClick={openCreateProfile}>Nova voz</Button>
            <Button variant="outlined" disabled={loading} onClick={loadProfiles}>Recarregar</Button>
          </Box>
          <Typography variant="body2" color="text.secondary">
            Perfis locais foram ocultados e permanecem apenas armazenados como legado desativado.
          </Typography>
          <DataTable columns={profilesColumns} rows={profiles} getKey={(r) => r.id} />
        </Box>
      ) : null}

      {tab === 3 ? (
        <Box display="grid" gap={2}>
          <Box display="grid" gap={2} gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr 1fr' }}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select label="Status" value={jobsStatus} onChange={(e) => setJobsStatus(e.target.value)}>
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="queued">queued</MenuItem>
                <MenuItem value="running">running</MenuItem>
                <MenuItem value="retrying">retrying</MenuItem>
                <MenuItem value="failed">failed</MenuItem>
                <MenuItem value="completed">completed</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Voz</InputLabel>
              <Select label="Voz" value={jobsVoiceProfileId} onChange={(e) => setJobsVoiceProfileId(e.target.value)}>
                <MenuItem value="">Todas</MenuItem>
                {profiles.map((p) => (
                  <MenuItem key={p.id} value={String(p.id)}>{`#${p.id} - ${p.name}`}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
              <Button variant="outlined" disabled={loading} onClick={loadJobs}>Atualizar</Button>
              <Button variant="outlined" disabled={loading} onClick={loadProfiles}>Atualizar vozes</Button>
            </Box>
          </Box>
          <DataTable columns={jobsColumns} rows={jobs} getKey={(r) => r.id} />
          <Typography variant="body2" color="text.secondary">
            {jobs.length} jobs
          </Typography>
        </Box>
      ) : null}

      <Dialog open={profileDialogOpen} onClose={() => setProfileDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingProfileId ? `Editar voz #${editingProfileId}` : 'Nova voz'}</DialogTitle>
        <DialogContent>
          <Box mt={1} display="grid" gap={2} gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }}>
            <TextField label="Nome" value={profileForm.name} onChange={(e) => setProfileForm((p) => ({ ...p, name: e.target.value }))} fullWidth />
            <FormControl fullWidth>
              <InputLabel>Provider</InputLabel>
              <Select label="Provider" value={profileForm.provider} onChange={(e) => setProfileForm((p) => ({ ...p, provider: e.target.value }))}>
                {providers.map((p) => (
                  <MenuItem key={p.key} value={p.key}>{p.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField label="Provider Voice ID" value={profileForm.providerVoiceId} onChange={(e) => setProfileForm((p) => ({ ...p, providerVoiceId: e.target.value }))} fullWidth />
            <TextField label="Idioma" value={profileForm.language} onChange={(e) => setProfileForm((p) => ({ ...p, language: e.target.value }))} fullWidth />
            <TextField label="Gênero (opcional)" value={profileForm.gender} onChange={(e) => setProfileForm((p) => ({ ...p, gender: e.target.value }))} fullWidth />
            <TextField label="Categoria (opcional)" value={profileForm.category} onChange={(e) => setProfileForm((p) => ({ ...p, category: e.target.value }))} fullWidth />
            <Box display="flex" alignItems="center" gap={1}>
              <Switch checked={profileForm.enabled === true} onChange={(e) => setProfileForm((p) => ({ ...p, enabled: e.target.checked }))} />
              <Typography>Ativa</Typography>
            </Box>
          </Box>
          <Box mt={2}>
            <TextField
              label="defaultsJson"
              value={profileForm.defaultsJson}
              onChange={(e) => setProfileForm((p) => ({ ...p, defaultsJson: e.target.value }))}
              fullWidth
              multiline
              minRows={6}
            />
          </Box>
          <Box mt={2}>
            <Typography variant="body2" color="text.secondary">
              Exemplo: {"{ \"speed\": 1, \"emotion\": 0.5, \"voiceSettings\": { ... } }"}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProfileDialogOpen(false)}>Cancelar</Button>
          <Button variant="contained" disabled={loading} onClick={saveProfile}>Salvar</Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
}
